import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress, 
  IconButton, 
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import useTablePaginationInit from '../../../components/hooks/useTablePaginationInit';
import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import TableTimeStamps from '../../../components/utils/TableTimeStamps';
import Copyable from '../../../components/utils/Copyable';
import TopBar from '../../../components/utils/TopBar';
import NA from '../../../components/utils/NA';

import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined';

import { 
  GetContactRequestsWT, 
  UpdateContactRequestsWT 
} from '../../../redux/actions/watchTower/contactRequestActions';

import style from "../../../global.scss";
import "./styles.scss";

const ContactRequestsPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state.watchTower);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [markLoading, setMarkLoading] = useState(false);
  const [markId, setMarkId] = useState(null);

  const [contactRequestsList, setContactRequestsList] = useState([]);
  const [contactRequestsTotal, setContactRequestsTotal] = useState(0);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Name",
      sort: "name",
    },
    {
      label: "Email",
      sort: "email",
    },
    {
      label: "Message",
      sort: "message",
    },
    {
      label: "Time stamps",
      sort: "createdAt",
    },
    {
      label: "Mark read",
      sort: "mark_read",
    }
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetContactRequests();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatContactRequestsList(state.contactRequestsList.records || []);
      setContactRequestsTotal(state.contactRequestsList.total || 0);
    } catch (err) {
      console.log(err);
    }
  }, [state]);

  // ======================== list formatter ========================
  const formatContactRequestsList = (list) => {
    setContactRequestsList(list);
  };
  
  async function handleGetContactRequests(customLoading){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    dispatch(GetContactRequestsWT(page+1, rowsPerPage, query, customLoading ? customLoading : setLoading));
  };

  const handleMarkRead = (id) => {
    setMarkId(id);
    dispatch(UpdateContactRequestsWT(id, setMarkLoading)).then(() => {
      handleGetContactRequests(setMarkLoading).then(() => {
        setMarkId(null);
      });
    })
  }

  return (
    <Box width={"100%"}>

      <TopBar 
        title={"Contact Requests"}
        />
      {
        loading
        ?
        <Box height={"200px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          contactRequestsList.length === 0
          ?
          <Box height={"200px"} className="flexCenterCenterRow">
            <Typography>No Contact Requests Found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                contactRequestsList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.message}</TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.createdAt}
                        updatedAt={row.updatedAt}
                        />
                    </TableCell>
                    <TableCell>
                      {
                        markId === row.id && markLoading
                        ?
                        <CircularProgress size={34} />
                        :
                        <IconButton
                          onClick={() => {
                            handleMarkRead(row.id);
                          }}>
                          {
                            row.mark_read
                            ?
                            <DraftsOutlinedIcon sx={{ color: style['secondary_light'] }} />
                            :
                            <MarkunreadOutlinedIcon sx={{ color: style['primary_light'] }} />
                          }
                        </IconButton>
                      }
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={contactRequestsTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default ContactRequestsPage